'use client';

import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';
import FullPageSpinner from '@/app/components/loading/FullPageSpinner';

/**
 * Flat client route registry for the workspace shell (spec:
 * workspace-static-spa-routing).
 *
 * Maps a normalized sub-path key (from `parseWorkspacePath`) to the view that
 * renders inside the AppShell. The views are the existing route `PageClient`
 * components reused as-is — they use no `next/navigation` hooks, so they need
 * no changes. The home view loads eagerly for first paint; every other view is
 * `dynamic()`-loaded to keep the shell bundle small (one chunk per view, on
 * demand — the same code-splitting Next gave the old file-based routes).
 *
 * Adding a workspace page: add its `PageClient` here under its sub-path key.
 */

// Home — eager for first paint.
import WorkspaceHomeView from '@/app/workspace/[tenantCode]/PageClient';

const loading = () => <FullPageSpinner />;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazy = (loader: () => Promise<any>): ComponentType =>
  dynamic(loader, { loading });

export const workspaceViewRegistry: Record<string, ComponentType> = {
  '': WorkspaceHomeView,

  // Apps
  'apps/mlinkone': lazy(() => import('@/app/workspace/[tenantCode]/apps/mlinkone/PageClient')),
  'apps/pack-analysis': lazy(() => import('@/app/workspace/[tenantCode]/apps/pack-analysis/PageClient')),

  // Settings
  'settings/active-party-defaults': lazy(() => import('@/app/workspace/[tenantCode]/settings/active-party-defaults/PageClient')),
  'settings/affiliation-types': lazy(() => import('@/app/workspace/[tenantCode]/settings/affiliation-types/PageClient')),
  'settings/approved-cartons': lazy(() => import('@/app/workspace/[tenantCode]/settings/approved-cartons/PageClient')),
  'settings/edi-archive': lazy(() => import('@/app/workspace/[tenantCode]/settings/edi-archive/PageClient')),
  'settings/edi-field-mapping': lazy(() => import('@/app/workspace/[tenantCode]/settings/edi-field-mapping/PageClient')),
  'settings/line-display-profile': lazy(() => import('@/app/workspace/[tenantCode]/settings/line-display-profile/PageClient')),
  'settings/party-settings': lazy(() => import('@/app/workspace/[tenantCode]/settings/party-settings/PageClient')),
  'settings/sscc-settings': lazy(() => import('@/app/workspace/[tenantCode]/settings/sscc-settings/PageClient')),
  'settings/branding': lazy(() => import('@/app/workspace/[tenantCode]/settings/branding/PageClient')),
  'settings/carton-types': lazy(() => import('@/app/workspace/[tenantCode]/settings/carton-types/PageClient')),
  'settings/company-profile': lazy(() => import('@/app/workspace/[tenantCode]/settings/company-profile/PageClient')),
  'settings/edi-permissions': lazy(() => import('@/app/workspace/[tenantCode]/settings/edi-permissions/PageClient')),
  'settings/edi-roles': lazy(() => import('@/app/workspace/[tenantCode]/settings/edi-roles/PageClient')),
  'settings/item-master': lazy(() => import('@/app/workspace/[tenantCode]/settings/item-master/PageClient')),
  'settings/key-composition': lazy(() => import('@/app/workspace/[tenantCode]/settings/key-composition/PageClient')),
  'settings/packaging-rules': lazy(() => import('@/app/workspace/[tenantCode]/settings/packaging-rules/PageClient')),
  'settings/parties': lazy(() => import('@/app/workspace/[tenantCode]/settings/parties/PageClient')),
  'settings/party-members': lazy(() => import('@/app/workspace/[tenantCode]/settings/party-members/PageClient')),
  'settings/party-definitions': lazy(() => import('@/app/workspace/[tenantCode]/settings/party-definitions/PageClient')),
  'settings/trading-partner-enrollments': lazy(() => import('@/app/workspace/[tenantCode]/settings/trading-partner-enrollments/PageClient')),
  'settings/uom-ladders': lazy(() => import('@/app/workspace/[tenantCode]/settings/uom-ladders/PageClient')),

  // Tools
  'tools/barcode-generator': lazy(() => import('@/app/workspace/[tenantCode]/tools/barcode-generator/PageClient')),
  'tools/simple-notes': lazy(() => import('@/app/workspace/[tenantCode]/tools/simple-notes/PageClient')),
  'tools/weather-forecast': lazy(() => import('@/app/workspace/[tenantCode]/tools/weather-forecast/PageClient')),
};

/**
 * In-app context resolved from a sub-path key (spec: app-scoped-left-nav).
 *
 * App pages own their inner sections via the URL: a menu item points at
 * `…/apps/{appCode}/{sectionKey}`, which is NOT registered as its own view —
 * it resolves to the single registered app view by **longest-prefix match**.
 * The matched key is the `appKey`; everything after it is the `sectionKey`
 * (which the app reads via `useAppSection` to pick the inner panel).
 */
export interface AppContext {
  /** The resolved view component, or null when nothing matches. */
  view: ComponentType | null;
  /** The registry key that matched (the longest registered prefix). */
  viewKey: string;
  /** `viewKey` when it is an app (`apps/…`), else null. */
  appKey: string | null;
  /** Sub-path after `appKey` (e.g. `pack-planner`, `master-data/item-master`); '' when none. */
  sectionKey: string;
}

/**
 * Resolve the view + in-app context for a sub-path key.
 *
 * Tries the most-specific key first and walks down to the shortest non-empty
 * prefix until a registry hit; an unmatched deep path returns `view: null`
 * (it does NOT silently fall back to the home view — that would mask 404s).
 * `apps/pack-analysis/pack-planner` → matches `apps/pack-analysis`, with
 * `sectionKey = 'pack-planner'`.
 */
export function resolveAppContext(subPathKey: string): AppContext {
  const parts = subPathKey.split('/').filter(Boolean);
  for (let i = parts.length; i >= 1; i--) {
    const key = parts.slice(0, i).join('/');
    const view = workspaceViewRegistry[key];
    if (view) {
      const isApp = key.startsWith('apps/');
      return {
        view,
        viewKey: key,
        appKey: isApp ? key : null,
        sectionKey: isApp ? parts.slice(i).join('/') : '',
      };
    }
  }
  // Only the exact home key ('') falls through here; unknown deep paths → null.
  const homeView = subPathKey === '' ? workspaceViewRegistry[''] ?? null : null;
  return { view: homeView, viewKey: homeView ? '' : '', appKey: null, sectionKey: '' };
}

/** Resolve the view component for a sub-path key, or null if unregistered. */
export function resolveWorkspaceView(subPathKey: string): ComponentType | null {
  return resolveAppContext(subPathKey).view;
}
